// src/services/trackingWebSocket.js
// Simple singleton WebSocket client for vehicle tracking
// Connects to wss://aeroviewbackend.dockyardsoftware.com:8095/location
// Uses the same location WebSocket that drivers send updates to

class TrackingWebSocket {
  constructor() {
    this.ws = null;
    this.url = "wss://aeroviewbackend.dockyardsoftware.com:8095/location";
    this.authKey = null;
    this.subscribers = {}; // vId -> Set(callback)
    this.reconnectTimer = null;
    this.reconnectAttempts = 0;
  }

  connect(authKey) {
    if (!authKey) {
      console.warn("[TrackingWS] Missing authKey");
      return;
    }
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return;

    this.authKey = authKey;
    const urlWithToken = this.url + (this.url.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(authKey);
    console.log('[TrackingWS] Connecting to', this.url);

    try {
      this.ws = new WebSocket(urlWithToken);

      this.ws.onopen = () => {
        console.log('[TrackingWS] Connected to location WebSocket');
        this.reconnectAttempts = 0;
        // Send auth message
        try { 
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ type: 'auth', token: authKey, action: 'subscribe_tracking' })); 
          }
        } catch (e) {
          console.error('[TrackingWS] Auth send failed:', e);
        }
      };

      this.ws.onmessage = (evt) => {
        if (!evt.data) return;
        let msg;
        try { msg = JSON.parse(evt.data); } catch (e) { return; }
        
        // Handle different message formats from location WebSocket
        this.handleMessage(msg);
      };

      this.ws.onerror = (err) => {
        console.error('[TrackingWS] Error', err);
      };

      this.ws.onclose = () => {
        console.log('[TrackingWS] Closed');
        this.ws = null;
        // reconnect with backoff if subscribers exist
        if (Object.keys(this.subscribers).length > 0) {
          this.reconnectAttempts += 1;
          const timeout = Math.min(30000, 1000 * Math.pow(1.5, this.reconnectAttempts));
          console.log(`[TrackingWS] Reconnecting in ${timeout}ms`);
          this.reconnectTimer = setTimeout(() => this.connect(this.authKey), timeout);
        }
      };
    } catch (err) {
      console.error('[TrackingWS] Connect failed', err);
    }
  }

  handleMessage(msg) {
    if (!msg) return;

    // Handle array of messages
    if (Array.isArray(msg)) {
      msg.forEach((m) => this.handleMessage(m));
      return;
    }

    // Handle payload wrapper
    if (msg.payload) {
      if (Array.isArray(msg.payload)) {
        msg.payload.forEach((m) => this.handleMessage(m));
      } else {
        this.handleMessage(msg.payload);
      }
      return;
    }

    // Extract vehicle ID (try multiple field names)
    const vid = msg.vehicleId ?? msg.vId ?? msg.vehicle_id ?? msg.id ?? msg.VehicleId;
    // Extract driver info if available
    const driverSno = msg.driverSno ?? msg.driver_sno ?? msg.ServiceNo ?? msg.userId;

    if (!vid && !driverSno) return;

    // Extract coordinates (try multiple field names)
    const lat = msg.latitude ?? msg.lat ?? msg.Latitude ?? msg.Lat;
    const lon = msg.longitude ?? msg.lng ?? msg.lon ?? msg.Longitude ?? msg.Lon;
    
    // Check if this is an online/active vehicle
    const status = (msg.status ?? msg.Status ?? "").toString().toLowerCase();
    const action = (msg.action ?? "").toString().toLowerCase();
    const oStatus = (msg.oStatus ?? "").toString();

    let isOnline = msg.online === true || msg.isOnline === true || 
                     status === "active" || status === "online" || 
                     action === "online" || msg.type === "location_update" ||
                     action === "heartbeat";

    // Explicitly handle offline signals
    if (action === "offline" || status === "offline" || status === "inactive" || oStatus === "F") {
      isOnline = false;
    }

    // Build payload for subscribers
    const payload = { 
      vehicleId: vid,
      driverSno: driverSno, 
      latitude: (lat !== undefined) ? parseFloat(lat) : null,
      longitude: (lon !== undefined) ? parseFloat(lon) : null,
      isOnline: isOnline,
      timestamp: msg.timestamp || new Date().toISOString(),
      raw: msg 
    };

    console.log(`[TrackingWS] Location update for ${vid}: (${lat}, ${lon})`);

    // Dispatch to subscribers for this vehicle
    const subs = this.subscribers[vid];
    if (subs && subs.size > 0) {
      subs.forEach((cb) => {
        try { cb(payload); } catch (e) { console.error('[TrackingWS] Subscriber error', e); }
      });
    }

    // Also dispatch to "ALL" subscribers (for pages that want all vehicle updates)
    const allSubs = this.subscribers["ALL"];
    if (allSubs && allSubs.size > 0) {
      allSubs.forEach((cb) => {
        try { cb(payload); } catch (e) { console.error('[TrackingWS] ALL subscriber error', e); }
      });
    }
  }

  disconnect() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (!this.ws) return;
    try { this.ws.close(); } catch (e) {}
    this.ws = null;
  }

  subscribe(vId, cb) {
    if (!vId || typeof cb !== 'function') return () => {};
    if (!this.subscribers[vId]) this.subscribers[vId] = new Set();
    this.subscribers[vId].add(cb);
    console.log(`[TrackingWS] Subscribed to vehicle: ${vId}`);
    
    // If first subscriber, ensure connection
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      if (this.authKey) this.connect(this.authKey);
    }
    
    // Return unsubscribe function
    return () => this.unsubscribe(vId, cb);
  }

  unsubscribe(vId, cb) {
    if (!vId || !this.subscribers[vId]) return;
    this.subscribers[vId].delete(cb);
    console.log(`[TrackingWS] Unsubscribed from vehicle: ${vId}`);
    if (this.subscribers[vId].size === 0) delete this.subscribers[vId];
    // If no subscribers remain, disconnect
    if (Object.keys(this.subscribers).length === 0) {
      this.disconnect();
    }
  }

  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

const trackingWS = new TrackingWebSocket();
if (typeof window !== 'undefined') window.trackingWS = trackingWS;
export default trackingWS;
